import React from 'react';
import { injectIntl } from 'react-intl';
import AdminShell, { Icon } from './AdminShell';
import DataTable from './DataTable';
import { Modal, Drawer, Checklist, Summary, Timeline, Tabs, EmptyState } from './Overlay';
import { liveJobsCopy } from './liveJobsCopy';

/*
 * Direct bookings — antrean yang sudah dibayar dan menunggu provider.
 *
 * Beda dari Live jobs: layar ini punya AKSI. Tiap baris menunggu seseorang
 * menugaskan provider, dan itu satu-satunya alasan halaman ini ada.
 *
 * Syarat antreannya milik `getDirectBookings` (bookingType 3, paymentStatus
 * 'completed', status 'created') — sama persis dengan yang dihitung banner di
 * Dispatch dan badge di sidebar. Kalau salah satu diubah, ubah semuanya.
 */

const gql = (query, variables) => fetch('/graphql', {
  method: 'post',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  body: JSON.stringify({ query, variables }),
  credentials: 'include'
}).then(r => r.json());

const Q = `query ($currentPage: Int, $limit: Int, $searchList: String) {
  getDirectBookings(currentPage: $currentPage, limit: $limit, searchList: $searchList) {
    count status errorMessage
    results {
      id totalFare currency createdAt userLocation paymentStatus partnerId
      userDetails { firstName lastName }
      categoryDetails { name }
    }
  }
}`;

const Q_PROVIDERS = `{
  getAssignableProviders { status result { id name isActive } }
}`;

const M_ASSIGN = `mutation ($bookingId: Int!, $partnerId: String!) {
  assignBooking(bookingId: $bookingId, partnerId: $partnerId) { status errorMessage }
}`;

const Q_META = `{ getDirectQueueMeta { medianWaitMins window } }`;

const M_NEAREST = `mutation ($dryRun: Boolean) {
  assignNearestProviders(dryRun: $dryRun) {
    status errorMessage assigned skipped
    pairs { bookingId providerId providerName distanceKm online serviceName etaMins skipReason }
  }
}`;

// Jendela yang benar-benar dipakai untuk mediannya. Prototipe menulis "today";
// menyebut "today" di atas angka bulan lalu itu berbohong lewat label.
const DIRECT_COPY = {
  en: {
    page: {
      breadcrumb: ['Operations', 'Direct bookings'], title: 'Direct bookings',
      lead: 'Paid up front and waiting for a provider. This is the queue that costs money when it is ignored.'
    },
    paid: 'Paid', placed: 'Placed', requested: 'Requested', assign: 'Assign',
    assignAllNearest: 'Assign all nearest', workingOut: 'Working out…', viewBooking: 'View booking',
    alreadyPaid: 'These customers have already paid.',
    noWait: 'No booking has been assigned yet, so there is no wait to compare against.',
    windows: { today: 'today', '30d': 'over the last 30 days', all: 'all time' },
    medianWait: (window, mins) => `Median wait before assignment ${window} is ${mins} minute${mins === 1 ? '' : 's'}.`,
    planTitle: n => `Assign ${n} booking${n === 1 ? '' : 's'}`,
    planSub: 'Each goes to its nearest available provider.', noProvider: 'no provider',
    staysQueued: n => `${n} booking${n === 1 ? '' : 's'} will be skipped and stay in the queue.`,
    nearestError: 'Could not work out the nearest providers.', queueClearNotice: 'Nothing to assign — the queue is clear.',
    nearestResult: n => `${n} booking${n === 1 ? '' : 's'} assigned to the nearest provider.`,
    skippedResult: n => ` ${n} skipped and left in the queue.`, loadError: 'Could not load the queue.',
    assignBookingError: 'Could not assign this booking.', directSearch: 'Search reference, customer or area',
    queueClear: 'Queue is clear', queueClearHint: 'Every paid booking has a provider assigned.',
    type: 'Type', statusLabel: 'Status', eta: n => `ETA ${n}m`,
    assignTitle: id => `Assign BK-${id}`, provider: 'Provider', waiting: 'waiting',
    confirmAssignment: 'Confirm assignment', assigning: 'Assigning…',
    noReviewMsg: 'Reviews become available after the job is completed.',
    noMessages: 'No messages yet', noMessagesMsg: 'Chat becomes available after a provider is assigned.',
    skipReasons: {}
  },
  ar: {
    page: {
      breadcrumb: ['العمليات', 'الحجوزات المباشرة'], title: 'الحجوزات المباشرة',
      lead: 'مدفوعة مقدمًا وتنتظر تعيين مقدم خدمة.'
    },
    paid: 'المدفوع', placed: 'وقت الطلب', requested: 'مطلوب', assign: 'تعيين',
    assignAllNearest: 'تعيين الأقرب للجميع', workingOut: 'جارٍ البحث…', viewBooking: 'عرض الحجز',
    alreadyPaid: 'دفع هؤلاء العملاء مقدمًا.',
    noWait: 'لم يتم تعيين أي حجز بعد، لذلك لا توجد مدة انتظار للمقارنة.',
    windows: { today: 'اليوم', '30d': 'خلال آخر 30 يومًا', all: 'طوال الفترة' },
    medianWait: (window, mins) => `وسيط مدة الانتظار قبل التعيين ${window} هو ${mins} دقيقة.`,
    planTitle: n => `تعيين ${n} من الحجوزات`,
    planSub: 'سيُعيّن كل حجز إلى أقرب مقدم خدمة متاح.', noProvider: 'لا يوجد مقدم خدمة',
    staysQueued: n => `سيتم تخطي ${n} من الحجوزات وستبقى في قائمة الانتظار.`,
    nearestError: 'تعذر العثور على أقرب مقدمي الخدمة.', queueClearNotice: 'لا يوجد ما يمكن تعيينه — قائمة الانتظار فارغة.',
    nearestResult: n => `تم تعيين ${n} من الحجوزات إلى أقرب مقدمي الخدمة.`,
    skippedResult: n => ` تم تخطي ${n} وإبقاؤها في قائمة الانتظار.`, loadError: 'تعذر تحميل قائمة الانتظار.',
    assignBookingError: 'تعذر تعيين هذا الحجز.', directSearch: 'ابحث بالرقم المرجعي أو العميل أو المنطقة',
    queueClear: 'قائمة الانتظار فارغة', queueClearHint: 'تم تعيين مقدم خدمة لكل حجز مدفوع.',
    type: 'النوع', statusLabel: 'الحالة', eta: n => `الوصول المتوقع ${n} د`,
    assignTitle: id => `تعيين الحجز BK-${id}`, provider: 'مقدم الخدمة', waiting: 'مدة الانتظار',
    confirmAssignment: 'تأكيد التعيين', assigning: 'جارٍ التعيين…',
    noReviewMsg: 'تتوفر التقييمات بعد اكتمال العمل.',
    noMessages: 'لا توجد رسائل بعد', noMessagesMsg: 'تتوفر المحادثة بعد تعيين مقدم خدمة.',
    skipReasons: {
      'No pickup location recorded on this booking': 'لا يوجد موقع استلام مسجل لهذا الحجز',
      'No available online provider serves this booking category': 'لا يوجد مقدم خدمة متصل ومتاح لهذه الفئة',
      'Assignment failed; booking stayed in the queue': 'فشل التعيين وبقي الحجز في قائمة الانتظار'
    }
  }
};

const directCopy = locale => ({
  ...liveJobsCopy(locale),
  ...DIRECT_COPY[locale === 'ar' ? 'ar' : 'en']
});

const name = p => (p ? `${p.firstName || ''} ${p.lastName || ''}`.trim() : '');

const since = (v, copy) => {
  const ms = Date.now() - new Date(Number(v) || v).getTime();
  if (isNaN(ms)) return '—';
  const m = Math.round(ms / 60000);
  return copy.age(m);
};

class DirectBookings extends React.Component {
  state = {
    rows: [], count: 0, page: 1, perPage: 10, search: '',
    loading: true, error: null,
    providers: [],
    // Booking yang sedang ditugaskan, plus provider yang dipilih untuknya.
    assigning: null, chosen: '', saving: false, notice: null,
    meta: null, plan: null, planning: false, viewing: null, tab: 0
  };

  componentDidMount() {
    this.load();
    this.loadProviders();
    this.loadMeta();
    this.timer = setInterval(() => this.load(true), 30000);
  }
  componentWillUnmount() { clearInterval(this.timer); }

  copy = () => directCopy(this.props.intl.locale);

  load = async quiet => {
    if (!quiet) this.setState({ loading: true, error: null });
    const json = await gql(Q, {
      currentPage: this.state.page,
      limit: this.state.perPage,
      searchList: this.state.search || undefined
    }).catch(() => null);
    const res = json && json.data && json.data.getDirectBookings;
    if (!res || res.status !== 200) {
      this.setState({ loading: false, error: (res && res.errorMessage) || this.copy().loadError });
      return;
    }
    this.setState({ rows: res.results || [], count: res.count || 0, loading: false });
  };

  loadMeta = async () => {
    const json = await gql(Q_META).catch(() => null);
    const m = json && json.data && json.data.getDirectQueueMeta;
    if (m) this.setState({ meta: m });
  };

  planNearest = async () => {
    this.setState({ planning: true, notice: null });
    const json = await gql(M_NEAREST, { dryRun: true }).catch(() => null);
    const res = json && json.data && json.data.assignNearestProviders;
    this.setState({ planning: false });
    if (!res || res.status !== 200) {
      this.setState({ notice: { tone: 'danger', text: (res && res.errorMessage) || this.copy().nearestError } });
      return;
    }
    if (!res.pairs.length) {
      this.setState({ notice: { tone: 'amber', text: this.copy().queueClearNotice } });
      return;
    }
    this.setState({ plan: res });
  };

  runNearest = async () => {
    this.setState({ saving: true });
    const json = await gql(M_NEAREST, { dryRun: false }).catch(() => null);
    const res = json && json.data && json.data.assignNearestProviders;
    this.setState({ saving: false, plan: null });
    if (!res || res.status !== 200) {
      this.setState({ notice: { tone: 'danger', text: (res && res.errorMessage) || this.copy().assignError } });
      return;
    }
    this.setState({
      notice: {
        tone: 'amber',
        text: this.copy().nearestResult(res.assigned)
          + (res.skipped ? this.copy().skippedResult(res.skipped) : '')
      }
    });
    this.load(true);
    this.loadMeta();
  };

  loadProviders = async () => {
    const json = await gql(Q_PROVIDERS).catch(() => null);
    const res = json && json.data && json.data.getAssignableProviders;
    if (res && res.result) this.setState({ providers: res.result });
  };

  setPage = page => this.setState({ page }, () => this.load());
  setPerPage = perPage => this.setState({ perPage, page: 1 }, () => this.load());
  setSearch = search => this.setState({ search, page: 1 }, () => this.load());

  /*
   * Penugasan mengubah pekerjaan orang lain: begitu tersimpan, seorang provider
   * mendapat pekerjaan dan pelanggan mendapat sopir. Karena itu ada langkah
   * pilih-lalu-konfirmasi, bukan satu klik dari daftar — salah pilih tidak bisa
   * dibatalkan dari layar ini.
   */
  assign = async () => {
    const { assigning, chosen } = this.state;
    if (!assigning || !chosen) return;
    this.setState({ saving: true });
    const json = await gql(M_ASSIGN, { bookingId: assigning.id, partnerId: chosen }).catch(() => null);
    const res = json && json.data && json.data.assignBooking;
    this.setState({ saving: false });

    if (!res || res.status !== 200) {
      this.setState({ notice: { tone: 'danger', text: (res && res.errorMessage) || this.copy().assignBookingError } });
      return;
    }
    const t = this.copy();
    const who = (this.state.providers.find(p => p.id === chosen) || {}).name || t.providerFallback;
    this.setState({
      assigning: null, chosen: '',
      notice: { tone: 'amber', text: t.assigned(assigning.id, who) }
    });
    this.load(true);
  };

  render() {
    const {
      rows, count, page, perPage, loading, error,
      providers, assigning, chosen, saving, notice, meta, plan, planning, viewing, tab
    } = this.state;
    const t = this.copy();

    const columns = [
      { key: 'id', label: t.reference, sortable: true, render: r => <span className="mono">BK-{r.id}</span> },
      {
        key: 'customer', label: t.customer,
        sortValue: r => name(r.userDetails),
        render: r => name(r.userDetails) || <span className="muted">{t.unknown}</span>
      },
      {
        key: 'service', label: t.service, sortable: true,
        sortValue: r => (r.categoryDetails && r.categoryDetails.name) || '',
        render: r => (r.categoryDetails && r.categoryDetails.name) || '—'
      },
      {
        // Prototipe menamainya Area. Booking tidak punya kolom area — yang ada
        // alamat teks bebas, dan itu yang ditampilkan.
        key: 'userLocation', label: t.area,
        render: r => (
          <span title={r.userLocation || ''} style={{
            display: 'inline-block', maxWidth: 240, overflow: 'hidden',
            textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom'
          }}>{r.userLocation || '—'}</span>
        )
      },
      {
        key: 'totalFare', label: t.paid, align: 'end', sortable: true,
        sortValue: r => Number(r.totalFare) || 0,
        render: r => `${r.currency || 'AED'} ${Math.round(Number(r.totalFare) || 0)}`
      },
      {
        key: 'createdAt', label: t.placed, align: 'end', sortable: true,
        sortValue: r => Number(r.createdAt) || 0,
        // Prototipe menampilkan jam. Ditambah umur tunggunya, karena itu yang
        // membuat antrean ini mendesak — dan yang lewat sejam diberi warna.
        render: r => {
          const d = new Date(Number(r.createdAt) || r.createdAt);
          const mins = (Date.now() - d.getTime()) / 60000;
          return (
            <span>
              <span className="mono">{isNaN(d) ? '—' : d.toTimeString().slice(0, 5)}</span>
              <b style={{ display: 'block', fontSize: 'var(--fs-xs)', color: mins > 60 ? 'var(--danger)' : 'var(--tx-3)' }}>
                {since(r.createdAt, t)}
              </b>
            </span>
          );
        }
      },
      {
        key: 'act', label: '', align: 'end',
        render: r => (
          <span style={{ display: 'inline-flex', gap: 6 }}>
            <button className="btn icon" type="button" title={t.viewBooking}
              aria-label={`${t.viewBooking} BK-${r.id}`}
              onClick={() => this.setState({ viewing: r, tab: 0 })}>
              <Icon name="eye" />
            </button>
            <button className="btn sm" type="button"
              onClick={() => this.setState({ assigning: r, chosen: '', notice: null })}>
              {t.assign}
            </button>
          </span>
        )
      }
    ];

    return (
      <AdminShell
        current="direct"
        breadcrumb={t.page.breadcrumb}
        title={t.page.title}
        lead={t.page.lead}
        actions={[
          <button className="btn" type="button" key="n"
            onClick={this.planNearest} disabled={planning || !count}>
            <Icon name="route" />{planning ? t.workingOut : t.assignAllNearest}
          </button>,
          <a className="btn ghost" href="/siteadmin/direct-bookings" key="c">{t.openClassic}</a>
        ]}
      >
        {/* Spanduk milik prototipe. Jendela mediannya disebut apa adanya —
            "today" hanya kalau memang ada penugasan hari ini. */}
        {count > 0 && meta && (
          <div className="notice amber">
            <span className="ni">★</span>
            <span className="nt">
              <b>{t.alreadyPaid}</b>{' '}
              {meta.medianWaitMins === null
                ? <em>{t.noWait}</em>
                : <em>{t.medianWait(t.windows[meta.window] || '', meta.medianWaitMins)}</em>}
            </span>
          </div>
        )}

        {notice && (
          <div className={`notice ${notice.tone}`}>
            <span className="ni">{notice.tone === 'danger' ? '!' : '✓'}</span>
            <span className="nt">{notice.text}</span>
            <button className="btn ghost sm" type="button"
              onClick={() => this.setState({ notice: null })}>{t.dismiss}</button>
          </div>
        )}

        {plan && (() => {
          const ok = plan.pairs.filter(p => p.providerId);
          return (
            <Modal
              narrow
              icon="route"
              title={t.planTitle(ok.length)}
              sub={t.planSub}
              confirmLabel={`${t.assign} ${ok.length}`}
              cancelLabel={t.cancel}
              workingLabel={t.working}
              busy={saving}
              hideConfirm={ok.length === 0}
              onConfirm={this.runNearest}
              onClose={() => this.setState({ plan: null })}
            >
              <Checklist items={plan.pairs.map(p => (p.providerId
                ? {
                  state: 'pass',
                  label: `BK-${p.bookingId} → ${p.providerName}`,
                  detail: [p.serviceName, `${p.distanceKm} km`, t.eta(p.etaMins)]
                    .filter(Boolean).join(' · ') + (p.online ? '' : ` · ${t.offline}`)
                }
                : {
                  state: 'fail',
                  label: `BK-${p.bookingId} → ${t.noProvider}`,
                  detail: t.skipReasons[p.skipReason] || p.skipReason
                }))} />
              {plan.skipped > 0 && (
                <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--tx-3)', marginTop: 12 }}>
                  {t.staysQueued(plan.skipped)}
                </p>
              )}
            </Modal>
          );
        })()}

        {viewing && (
          <Drawer
            title={`BK-${viewing.id}`}
            sub={[(viewing.categoryDetails || {}).name, viewing.userLocation,
              new Date(Number(viewing.createdAt) || viewing.createdAt).toTimeString().slice(0, 5)]
              .filter(Boolean).join(' \u00b7 ')}
            headExtra={<span className="tag warn"><i />{t.requested}</span>}
            tabs={<Tabs labels={[t.overview, t.review, t.chat]} active={tab}
              onChange={nextTab => this.setState({ tab: nextTab })} />}
            closeLabel={t.close}
            onClose={() => this.setState({ viewing: null })}
            actions={[
              <button className="btn ghost" type="button" key="c"
                onClick={() => this.setState({ viewing: null })}>{t.close}</button>,
              <button className="btn" type="button" key="a"
                onClick={() => this.setState({ assigning: viewing, viewing: null, chosen: '' })}>
                <Icon name="route" />{t.assignProvider}
              </button>
            ]}
          >
            {tab === 0 && <div>
              <Summary pairs={[
                  [t.reference, <span className="mono">BK-{viewing.id}</span>],
                  [t.statusLabel, <span className="tag warn"><i />{t.requested}</span>],
                  [t.type, t.direct],
                  [t.customer, name(viewing.userDetails) || t.unknown],
                  [t.provider, <span className="tag danger"><i />{t.unassigned}</span>],
                  [t.service, (viewing.categoryDetails || {}).name || '\u2014'],
                  [t.area, viewing.userLocation || '\u2014'],
                  [t.fare, `${viewing.currency || 'AED'} ${Math.round(Number(viewing.totalFare) || 0)}`]
              ]} />
              <div style={{
                fontSize: 'var(--fs-xs)', color: 'var(--tx-3)', letterSpacing: '.11em',
                textTransform: 'uppercase', fontWeight: 700, margin: '18px 0 12px'
              }}>{t.lifecycle}</div>
              {/* Hanya peristiwa dengan timestamp nyata dari query. */}
              <Timeline events={[
                {
                  title: t.bookingCreated,
                  detail: `${name(viewing.userDetails) || t.customerFallback} \u00b7 ${(viewing.categoryDetails || {}).name || t.serviceFallback}`,
                  at: new Date(Number(viewing.createdAt) || viewing.createdAt).toTimeString().slice(0, 5),
                  color: 'var(--info)'
                },
                {
                  title: t.paidUpFront,
                  detail: `${viewing.currency || 'AED'} ${Math.round(Number(viewing.totalFare) || 0)}`,
                  color: 'var(--ok)'
                }
              ]} />
            </div>}
            {tab === 1 && (
              <EmptyState icon="star" title={t.noReview}
                msg={t.noReviewMsg} />
            )}
            {tab === 2 && (
              <EmptyState icon="chat" title={t.noMessages}
                msg={t.noMessagesMsg} />
            )}
          </Drawer>
        )}

        {assigning && (
          <div className="card" style={{ marginBottom: 'var(--sp-4)' }}>
            <div className="card-h">
              <h2>{t.assignTitle(assigning.id)}</h2>
              <div className="sp">
                <button className="btn ghost sm" type="button"
                  onClick={() => this.setState({ assigning: null, chosen: '' })}>{t.cancel}</button>
              </div>
            </div>
            {/* Dua baris, bukan satu.
                Dulu tombolnya bersaudara langsung dengan `.field` di satu baris
                `align-items:flex-end`. `.field` berisi label + select + hint,
                jadi "ujung bawah" yang disejajarkan itu ujung bawah HINT —
                tombolnya melorot satu baris teks di bawah select.
                Sekarang barisnya cuma memuat label+select dan tombol, jadi
                keduanya benar-benar sebaris; hint turun ke barisnya sendiri. */}
            <div className="card-b" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
              <div style={{ display: 'flex', gap: 'var(--sp-4)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div className="field" style={{ flex: 1, minWidth: 260 }}>
                  <label>{t.provider}</label>
                  <select className="inp" value={chosen}
                    onChange={e => this.setState({ chosen: e.target.value })}>
                    <option value="">{t.chooseProvider}</option>
                    {/* Yang offline tetap ditawarkan, dan ditandai. Direct booking
                        justru untuk layanan yang tidak menuntut provider online —
                        admin menghubunginya sendiri. */}
                    {providers.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name || t.unnamed}{p.isActive ? '' : ` · ${t.offline}`}
                      </option>
                    ))}
                  </select>
                </div>
                <button className="btn" type="button" disabled={!chosen || saving}
                  onClick={this.assign}>
                  {saving ? t.assigning : t.confirmAssignment}
                </button>
              </div>
              <span className="hint">
                {name(assigning.userDetails)} · {(assigning.categoryDetails || {}).name || 'service'}
                {' · '}{t.waiting} {since(assigning.createdAt, t)}
              </span>
            </div>
          </div>
        )}

        {error
          ? <div className="notice danger"><span className="ni">!</span><span className="nt">{error}</span></div>
          : (
            <DataTable
              columns={columns}
              rows={rows}
              count={count}
              page={page}
              perPage={perPage}
              loading={loading}
              onPage={this.setPage}
              onPerPage={this.setPerPage}
              onSearch={this.setSearch}
              searchPlaceholder={t.directSearch}
              emptyText={t.queueClear}
              emptyHint={t.queueClearHint}
              copy={t}
            />
          )}
      </AdminShell>
    );
  }
}

export default injectIntl(DirectBookings);
